if (!customElements.get('roster-grid')) { 
  customElements.define(
    'roster-grid',
    class RosterGrid extends HTMLElement {
      constructor() {
        super();

        // State
        this.allWrestlers = [];
        this.displayedCount = 0;
        this.isLoading = false;
        this.hasMoreToLoad = true;
        this.displayedCountBeforeSearch = 0; // Track count before search
        this.displayedCountBeforeFilter = 0; // Track count before filter

        this.showCardOverlay = this.dataset.showCardOverlay === 'true';

        // Bind methods
        this.loadMore = this.loadMore.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleSearchInput = this.handleSearchInput.bind(this);
      }

      // Settings from data attributes
      get loadingStrategy() {
        return this.dataset.loadingStrategy || 'load_more';
      }

      get batchSize() {
        return parseInt(this.dataset.batchSize, 10) || 12;
      }

      get initialCount() {
        return parseInt(this.dataset.initialCount, 10) || 12;
      }

      get showGroups() {
        return this.dataset.showGroups === 'true';
      }

      get showFilters() {
        return this.dataset.showFilters || 'true'
      }

      get showSeparateFeaturedWrestlersSection() {
        return this.dataset.showSeparateFeaturedWrestlersSection === 'true';
      }

      get featuredBadgeText() {
        return this.dataset.featuredBadgeText || 'Featured';
      }

      connectedCallback() {
        // Cache DOM elements
        this.gridContainer = this.querySelector('.roster-grid__container');
        this.loadMoreButton = this.querySelector('.roster-grid__load-more');
        this.loadingIndicator = this.querySelector('.roster-grid__loading');
        this.endSentinel = this.querySelector('.roster-grid__end-sentinel');
        this.groupFilters = this.querySelector('#group-filters');
        this.promotionFilters = this.querySelector('#promotion-filters');
        this.showChampions = this.querySelector('#show-champions');
        this.searchInput = this.querySelector('#search-input');

        // Initialize
        this.init();

        // Setup keyboard navigation
        this.addEventListener('keydown', this.handleKeyDown);
      }

      disconnectedCallback() {
        // Cleanup
        this.removeEventListener('keydown', this.handleKeyDown);

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        if (this.loadMoreButton) {
          this.loadMoreButton.removeEventListener('click', this.loadMore);
        }
      }

      bindFiltersEvents() {
        this.groupFilters.addEventListener('change', this.handleFilterChange);
        this.promotionFilters.addEventListener('change', this.handleFilterChange);
        this.showChampions.addEventListener('change', this.handleFilterChange);
        this.searchInput.addEventListener('input', this.debounce(this.handleSearchInput, 300));
      }

      debounce (func, wait) {
        let timeout;

        return function (...args) {
            const context = this;
            const later = () => {
                func.apply (context, args)
            }

            clearTimeout(timeout)
            timeout = setTimeout(later, wait)
        }
      }

      handleSearchInput() {
        const searchTerm = this.searchInput.value.toLowerCase();
        const filteredWrestlers = this.filterWrestlers(this.allWrestlers);

        // searching by name, nickname, or faction name
        const filtered = filteredWrestlers.filter(wrestler => {
          const name = (wrestler.name || '').toLowerCase();
          const nickname = (wrestler.nickname || '').toLowerCase();
          const nameMatch = name.includes(searchTerm);
          const nicknameMatch = nickname.includes(searchTerm);
          const factionMatch = wrestler.factions && wrestler.factions.some(faction => 
            (faction.name || '').toLowerCase().includes(searchTerm)
          );
          
          return nameMatch || nicknameMatch || factionMatch;
        });

        this.gridContainer.innerHTML = '';
        
        // Save the current displayed count before search starts (only if search is active)
        if (searchTerm.trim() !== '' && this.displayedCountBeforeSearch === 0) {
          this.displayedCountBeforeSearch = this.displayedCount;
        }
        
        // When search is cleared, restore the previously displayed count
        const cardsToShow = searchTerm.trim() === '' && this.displayedCountBeforeSearch > 0
          ? Math.min(this.displayedCountBeforeSearch, filtered.length)
          : (this.showGroups ? filtered.length : Math.min(this.initialCount, filtered.length));
        
        // Reset the saved count when search is cleared
        if (searchTerm.trim() === '') {
          this.displayedCount = 0;
          const previousCount = this.displayedCountBeforeSearch;
          this.displayedCountBeforeSearch = 0; // Reset for next search
          
          // Determine if we should show groups
          const shouldGroup = this.showGroups && searchTerm.trim() === '';
          
          if (shouldGroup) {
            this.renderCards(filtered, false);
            this.displayedCount = filtered.length;
            this.hasMoreToLoad = false;
          } else {
            // Restore the previous scroll position context
            const cardsToRender = previousCount > 0 ? filtered.slice(0, previousCount) : filtered.slice(0, this.initialCount);
            this.renderCards(cardsToRender, false);
            this.displayedCount = cardsToRender.length;
            this.hasMoreToLoad = this.displayedCount < filtered.length;
          }
        } else {
          // Active search - show limited results
          this.displayedCount = 0;
          this.filteredWrestlers = filtered;
          this.sortWrestlers(filtered);
          
          const cardsToRender = this.showGroups ? filtered : filtered.slice(0, this.initialCount);
          const shouldGroup = this.showGroups && searchTerm.trim() === '';
          
          if (shouldGroup) {
            this.renderCards(cardsToRender, false);
            this.displayedCount = filtered.length;
            this.hasMoreToLoad = false;
          } else {
            this.renderCards(cardsToRender, false);
            this.displayedCount = Math.min(this.initialCount, filtered.length);
            this.hasMoreToLoad = this.displayedCount < filtered.length;
          }
        }

        // Hide load more button if no more to load or grouping is enabled
        if (this.loadMoreButton) {
          this.loadMoreButton.hidden = !this.hasMoreToLoad || this.showGroups;
        }

        // Announce filter results to screen readers
        this.announceFilterResults(filtered.length);

        if (this.hasMoreToLoad && !this.showGroups) {
          if (this.loadingStrategy === 'infinite_scroll') {
            this.setupInfiniteScroll();
          } else {
            this.setupLoadMoreButton();
          }
        }

        if ((!this.hasMoreToLoad || this.showGroups) && this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }

      handleFilterChange() {
        const filtered = this.filterWrestlers(this.allWrestlers);
        
        // Check if any filter is active
        const isFilterActive = 
          this.showChampions.checked ||
          (this.groupFilters.value && this.groupFilters.value !== '') ||
          (this.promotionFilters.value && this.promotionFilters.value !== '');

        // Save displayed count when applying filters for the first time
        if (isFilterActive && this.displayedCountBeforeFilter === 0) {
          this.displayedCountBeforeFilter = this.displayedCount;
        }

        this.gridContainer.innerHTML = '';
        this.filteredWrestlers = filtered;
        this.sortWrestlers(filtered);
        
        // Determine how many cards to show
        let cardsToShow;
        
        if (!isFilterActive && this.displayedCountBeforeFilter > 0) {
          // Filter was cleared - restore the previous count
          cardsToShow = Math.min(this.displayedCountBeforeFilter, filtered.length);
          const previousCount = this.displayedCountBeforeFilter;
          this.displayedCountBeforeFilter = 0; // Reset for next filter
          
          if (this.showGroups) {
            cardsToShow = filtered.length;
          } else {
            cardsToShow = previousCount;
          }
        } else {
          // Filter is active or first load - show initial batch
          cardsToShow = this.showGroups ? filtered.length : this.initialCount;
        }
        
        // When grouping is enabled, load all results at once
        const cardsToRender = this.showGroups ? filtered : filtered.slice(0, cardsToShow);
        
        this.renderCards(cardsToRender);
        this.displayedCount = this.showGroups ? filtered.length : Math.min(cardsToShow, filtered.length);
        this.hasMoreToLoad = !this.showGroups && this.displayedCount < filtered.length;

        // Hide load more button if no more to load or grouping is enabled
        if (this.loadMoreButton) {
          this.loadMoreButton.hidden = !this.hasMoreToLoad || this.showGroups;
        }

        // Announce filter results to screen readers
        this.announceFilterResults(filtered.length);

        if (this.hasMoreToLoad && !this.showGroups) {
          if (this.loadingStrategy === 'infinite_scroll') {
            this.setupInfiniteScroll();
          } else {
            this.setupLoadMoreButton();
          }
        }

        if ((!this.hasMoreToLoad || this.showGroups) && this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }

      async init() {
        try {
          // Show loading immediately on init
          this.showLoading();
          // Hide the grid container initially
          if (this.gridContainer) {
            this.gridContainer.style.opacity = '0';
          }

          const data = await this.fetchRosterData();
          
          this.allWrestlers = this.sortWrestlers(data);
          
          // Build dynamic filter options from data
          this.buildFilterOptions(this.allWrestlers);
          
          if (this.showFilters) {
            this.allWrestlers = this.filterWrestlers(this.allWrestlers);
          }
          this.bindFiltersEvents();

          // When grouping is enabled, load all results at once for better UX
          const cardsToRender = this.showGroups ? this.allWrestlers : this.allWrestlers.slice(0, this.initialCount);
          
          this.renderCards(cardsToRender);
          this.displayedCount = this.showGroups ? this.allWrestlers.length : Math.min(this.initialCount, this.allWrestlers.length);

          // Wait for images to load before showing content
          await this.waitForImages();

          // Hide loading, show grid
          this.hideLoading();
          if (this.gridContainer) {
            this.gridContainer.style.opacity = '1';
          }

          // Check if there's more to load (only when not grouping)
          this.hasMoreToLoad = !this.showGroups && this.displayedCount < this.allWrestlers.length;

          // Setup loading strategy only if not grouping
          if (this.hasMoreToLoad && !this.showGroups) {
            if (this.loadingStrategy === 'infinite_scroll') {
              this.setupInfiniteScroll();
            } else {
              this.setupLoadMoreButton();
            }
          } else if (this.loadMoreButton) {
            // Hide load more button when grouping is enabled
            this.loadMoreButton.hidden = true;
          }

          // Announce to screen readers
          this.announceLoad(this.displayedCount, this.allWrestlers.length);
        } catch (error) {
          console.error('Failed to initialize roster grid:', error);
          this.showError();
        }
      }

      async fetchRosterData() {
        const response = await fetch('/pages/roster?view=roster-data-template');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
      }

      buildFilterOptions(wrestlers) {
        // Extract unique groups and promotions from the data
        const groups = new Set();
        const promotions = new Set();

        wrestlers.forEach(wrestler => {
          if (wrestler.group) {
            groups.add(wrestler.group);
          }
          // Handle promotion as array
          if (Array.isArray(wrestler.promotion)) {
            wrestler.promotion.forEach(promo => {
              if (promo && promo.trim()) {
                promotions.add(promo.trim());
              }
            });
          } else if (wrestler.promotion) {
            // Fallback for single promotion (backwards compatibility)
            promotions.add(wrestler.promotion);
          }
        });

        // Define preferred order for groups
        const groupOrder = ['Men', 'Women', 'Faction', 'Coach', 'Broadcast', 'Referee'];
        
        // Sort groups by preferred order, then alphabetically
        const sortedGroups = Array.from(groups).sort((a, b) => {
          const aIndex = groupOrder.indexOf(a);
          const bIndex = groupOrder.indexOf(b);
          
          // If both are in the order array, sort by their position
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only a is in the order array, it comes first
          if (aIndex !== -1) return -1;
          // If only b is in the order array, it comes first
          if (bIndex !== -1) return 1;
          // Otherwise sort alphabetically
          return a.localeCompare(b);
        });

        // Sort promotions alphabetically
        const sortedPromotions = Array.from(promotions).sort();

        // Populate group filter
        if (this.groupFilters) {
          // Keep the "All Groups" option, remove any existing dynamic options
          const allOption = this.groupFilters.querySelector('option[value=""]');
          this.groupFilters.innerHTML = '';
          if (allOption) {
            this.groupFilters.appendChild(allOption);
          } else {
            const defaultOption = document.createElement('option');
            defaultOption.value = '';
            defaultOption.textContent = 'All Groups';
            this.groupFilters.appendChild(defaultOption);
          }

          sortedGroups.forEach(group => {
            const option = document.createElement('option');
            option.value = group;
            option.textContent = group;
            this.groupFilters.appendChild(option);
          });
        }

        // Populate promotion filter
        if (this.promotionFilters) {
          // Keep the "All Promotions" option, remove any existing dynamic options
          const allOption = this.promotionFilters.querySelector('option[value=""]');
          this.promotionFilters.innerHTML = '';
          if (allOption) {
            this.promotionFilters.appendChild(allOption);
          } else {
            const defaultOption = document.createElement('option');
            defaultOption.value = '';
            defaultOption.textContent = 'All Promotions';
            this.promotionFilters.appendChild(defaultOption);
          }

          sortedPromotions.forEach(promotion => {
            const option = document.createElement('option');
            option.value = promotion;
            option.textContent = promotion;
            this.promotionFilters.appendChild(option);
          });
        }
      }

      sortWrestlers(data) {
        // Sort: Featured first, then alphabetically by name
        return [...data].sort((a, b) => {
          // Featured wrestlers come first
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;

          // Then sort alphabetically by name
          return a.name.localeCompare(b.name);
        });
      }

      renderCards(wrestlers, append = false) {
        if (!this.gridContainer) return;

        // Show no results message if empty
        if (wrestlers.length === 0 && !append) {
          this.gridContainer.innerHTML = `
            <div class="roster-grid__no-results" role="status">
              <p>No wrestlers found matching the selected filters.</p>
            </div>
          `;
          return;
        }

        // Determine if we should show groups:
        // - When showGroups is enabled
        // - AND we're not appending (batching is incompatible with groups)
        // - AND either there's no search term OR search is empty
        const searchTerm = this.searchInput ? this.searchInput.value.trim() : '';
        const shouldShowGroups = this.showGroups && !append && searchTerm === '';

        if (shouldShowGroups) {
          // Render grouped by category
          this.renderGroupedCards(wrestlers);
          return;
        }

        const fragment = document.createDocumentFragment();

        wrestlers.forEach((wrestler) => {
          const card = this.createCard(wrestler);
          fragment.appendChild(card);
        });

        if (append) {
          this.gridContainer.appendChild(fragment);
        } else {
          this.gridContainer.innerHTML = '';
          this.gridContainer.appendChild(fragment);
        }
      }

      filterWrestlers(wrestlers) {
        let filteredWrestlers = wrestlers;

        if (this.showChampions.checked) {
          filteredWrestlers = filteredWrestlers.filter(wrestler => wrestler.championships.length > 0);
        }

        if (this.groupFilters.value && this.groupFilters.value !== 'All') {
          filteredWrestlers = filteredWrestlers.filter(wrestler => wrestler.group === this.groupFilters.value);
        }

        if (this.promotionFilters.value && this.promotionFilters.value !== 'All') {
          filteredWrestlers = filteredWrestlers.filter(wrestler => {
            // Handle promotion as array
            if (Array.isArray(wrestler.promotion)) {
              return wrestler.promotion.includes(this.promotionFilters.value);
            }
            // Fallback for single promotion (backwards compatibility)
            return wrestler.promotion === this.promotionFilters.value;
          });
        }

        return filteredWrestlers;
      }

      renderGroupedCards(wrestlers) {
        if (this.showSeparateFeaturedWrestlersSection) {
            const featuredWrestlers = wrestlers.filter(wrestler => wrestler.featured);
            featuredWrestlers.forEach((wrestler) => {
                const card = this.createCard(wrestler);
                this.gridContainer.appendChild(card);
            });
        }
        // Group wrestlers by their group property
        const groups = this.showSeparateFeaturedWrestlersSection ? this.groupBy(wrestlers.filter(wrestler => !wrestler.featured), 'group') : this.groupBy(wrestlers, 'group');
        const groupOrder = this.showSeparateFeaturedWrestlersSection ? [this.featuredBadgeText, 'Men', 'Women', 'Faction', 'Coach', 'Broadcast', 'Referee'] : ['Men', 'Women', 'Faction', 'Coach', 'Broadcast', 'Referee'];

        this.gridContainer.innerHTML = '';

        groupOrder.forEach((groupName) => {
            if (this.showSeparateFeaturedWrestlersSection && groupName === this.featuredBadgeText) {
                const featuredWrestlers = wrestlers.filter(wrestler => wrestler.featured);
                // Create group header
                const featuredHeader = document.createElement('div');
                featuredHeader.className = 'roster-grid__group';
                featuredHeader.innerHTML = `<h3 role="listitem" class="roster-grid__group-title">${this.escapeHtml(groupName)}</h3>`;
                this.gridContainer.appendChild(featuredHeader);

                featuredWrestlers.forEach((wrestler) => {
                    const card = this.createCard(wrestler);
                    this.gridContainer.appendChild(card);
                });
                return;
            }
          const groupWrestlers = groups[groupName];
          if (!groupWrestlers || groupWrestlers.length === 0) return;

          // Create group header
          const groupHeader = document.createElement('div');
          groupHeader.className = 'roster-grid__group';
          groupHeader.innerHTML = `<h3 role="listitem" class="roster-grid__group-title">${this.escapeHtml(groupName)}</h3>`;
          this.gridContainer.appendChild(groupHeader);

          // Add cards for this group
          groupWrestlers.forEach((wrestler) => {
            const card = this.createCard(wrestler);
            this.gridContainer.appendChild(card);
          });
        });

        // Handle wrestlers without a group
        const ungrouped = wrestlers.filter(
          (w) => !w.group || !groupOrder.includes(w.group)
        );
        if (ungrouped.length > 0) {
          const groupHeader = document.createElement('div');
          groupHeader.className = 'roster-grid__group';
          groupHeader.innerHTML = '<h3 role="listitem" class="roster-grid__group-title">Other</h3>';
          this.gridContainer.appendChild(groupHeader);

          ungrouped.forEach((wrestler) => {
            const card = this.createCard(wrestler);
            this.gridContainer.appendChild(card);
          });
        }
      }

      groupBy(array, key) {
        return array.reduce((result, item) => {
          const groupKey = item[key] || 'Other';
          (result[groupKey] = result[groupKey] || []).push(item);
          return result;
        }, {});
      }

      createCard(wrestler) {
        const article = document.createElement('article');
        article.className = 'roster-card';
        article.setAttribute('role', 'listitem');

        // Factions HTML (outside main link for separate clickability)
        const factionsHtml = this.createFactionsHtml(wrestler.factions);

        // Championships HTML
        const championshipsHtml = this.createChampionshipsHtml(wrestler.championships);

        // Featured badge
        const featuredBadge = wrestler.featured
          ? `<span class="roster-card__featured" aria-label="Featured wrestler">${this.escapeHtml(this.featuredBadgeText)}</span>`
          : '';

        // Optimize loading for first visible cards (LCP optimization)
        const isAboveTheFold = this.displayedCount < 8; // First 8 cards likely above fold
        const loadingAttr = isAboveTheFold ? 'eager' : 'lazy';
        const fetchpriorityAttr = isAboveTheFold ? 'fetchpriority="high"' : '';

        const hasSecondaryImage = wrestler.secondary_image_url && wrestler.secondary_image_url_large && wrestler.secondary_image_url !== '' && wrestler.secondary_image_url_large !== '';
        console.log('hasSecondaryImage', hasSecondaryImage, 'wrestler.secondary_image_url', wrestler.secondary_image_url, 'wrestler.secondary_image_url_large', wrestler.secondary_image_url_large);

        const secondaryImageHtml = hasSecondaryImage ? 
        `<img 
            src="${this.escapeHtml(wrestler.secondary_image_url)}"
            srcset="${this.escapeHtml(wrestler.secondary_image_url)} 600w, ${this.escapeHtml(wrestler.secondary_image_url_large || wrestler.secondary_image_url)} 800w"
            sizes="(max-width: 749px) 50vw, (max-width: 989px) 33vw, 25vw"
            alt=""
            loading="lazy"
            decoding="async"
            aria-hidden="true"
            class="roster-card__image roster-card__image--secondary"
          >`
        : '';

        // Build card HTML
        article.innerHTML = `
          ${factionsHtml}
          ${featuredBadge}
          <a href="${this.escapeHtml(wrestler.product_collection_url || '#')}" 
             class="roster-card__link"
             aria-label="View ${this.escapeHtml(wrestler.name)}'s profile">
            <img 
              src="${this.escapeHtml(wrestler.image_url)}"
              srcset="${this.escapeHtml(wrestler.image_url)} 600w, ${this.escapeHtml(wrestler.image_url_large || wrestler.image_url)} 800w"
              sizes="(max-width: 749px) 50vw, (max-width: 989px) 33vw, 25vw"
              alt=""
              loading="${loadingAttr}"
              ${fetchpriorityAttr}
              decoding="async"
              class="roster-card__image roster-card__image--primary"
            >
            ${secondaryImageHtml}
            ${this.showCardOverlay ? `<div class="roster-card__overlay" aria-hidden="true"></div>` : ''}
            <div class="roster-card__content">
              ${wrestler.nickname ? `<span class="roster-card__nickname">${this.escapeHtml(wrestler.nickname)}</span>` : ''}
              <h3 class="roster-card__name">${this.escapeHtml(wrestler.name)}</h3>
              ${championshipsHtml}
            </div>
          </a>
        `;

        return article;
      }

      createFactionsHtml(factions) {
        if (!factions || factions.length === 0) return '';

        const factionLinks = factions
          .filter((f) => f.name)
          .map(
            (faction) =>
              `<a href="${this.escapeHtml(faction.url || '#')}" 
                  class="roster-card__faction"
                  aria-label="View ${this.escapeHtml(faction.name)} faction"
                  onclick="event.stopPropagation();">
                ${this.escapeHtml(faction.name)}
              </a>`
          )
          .join('');

        return factionLinks
          ? `<div class="roster-card__factions">${factionLinks}</div>`
          : '';
      }

      createChampionshipsHtml(championships) {
        if (!championships || championships.length === 0) return '';

        const championshipItems = championships
          .filter((c) => c && c.trim())
          .map(
            (championship) =>
              `<span class="roster-card__championship">${this.escapeHtml(championship)}</span>`
          )
          .join('');

        return championshipItems
          ? `<div class="roster-card__championships" aria-label="Championships held">${championshipItems}</div>`
          : '';
      }

      setupLoadMoreButton() {
        if (!this.loadMoreButton) return;

        this.loadMoreButton.hidden = false;
        this.loadMoreButton.addEventListener('click', this.loadMore);
      }

      setupInfiniteScroll() {
        if (!this.endSentinel || !('IntersectionObserver' in window)) {
          // Fallback to load more button
          this.setupLoadMoreButton();
          return;
        }

        this.intersectionObserver = new IntersectionObserver(
          this.handleIntersection,
          {
            root: null,
            rootMargin: '200px',
            threshold: 0,
          }
        );

        this.intersectionObserver.observe(this.endSentinel);
      }

      handleIntersection(entries) {
        const [entry] = entries;

        if (entry.isIntersecting && !this.isLoading && this.hasMoreToLoad) {
          this.loadMore();
        }
      }

      async loadMore() {
        if (this.isLoading || !this.hasMoreToLoad) return;

        this.isLoading = true;
        this.showLoading();

        if (this.loadMoreButton) {
          this.loadMoreButton.disabled = true;
        }

        const nextBatch = this.allWrestlers.slice(
          this.displayedCount,
          this.displayedCount + this.batchSize
        );

        this.renderCards(nextBatch, true);
        
        // Wait for the new images to load
        await this.waitForImages();
        
        this.displayedCount += nextBatch.length;

        // Check if there's more
        this.hasMoreToLoad = this.displayedCount < this.allWrestlers.length;

        this.hideLoading();
        this.isLoading = false;

        if (this.loadMoreButton) {
          this.loadMoreButton.disabled = false;

          if (!this.hasMoreToLoad) {
            this.loadMoreButton.hidden = true;
          }
        }

        // Stop observing if no more to load
        if (!this.hasMoreToLoad && this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }

        // Announce to screen readers
        this.announceLoad(this.displayedCount, this.allWrestlers.length);
      }

      async waitForImages() {
        // Get all images that haven't finished loading
        const images = Array.from(this.gridContainer.querySelectorAll('img'));
        const imagePromises = images.map(img => {
          // If image is already loaded, resolve immediately
          if (img.complete) {
            return Promise.resolve();
          }
          // Otherwise wait for load or error
          return new Promise(resolve => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true }); // Resolve on error too so we don't hang
          });
        });

        // Wait for all images to load (or timeout after 5 seconds)
        await Promise.race([
          Promise.all(imagePromises),
          new Promise(resolve => setTimeout(resolve, 5000))
        ]);
      }

      showLoading() {
        if (this.loadingIndicator) {
          this.loadingIndicator.hidden = false;
        }
      }

      hideLoading() {
        if (this.loadingIndicator) {
          this.loadingIndicator.hidden = true;
        }
      }

      showError() {
        this.hideLoading();

        if (this.gridContainer) {
          this.gridContainer.innerHTML = `
            <div class="roster-grid__error" role="alert">
              <p>Unable to load roster. Please try again later.</p>
            </div>
          `;
        }
      }

      announceLoad(loaded, total) {
        // Create or update live region for screen readers
        let liveRegion = this.querySelector('.roster-grid__live-region');

        if (!liveRegion) {
          liveRegion = document.createElement('div');
          liveRegion.className = 'roster-grid__live-region visually-hidden';
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          this.appendChild(liveRegion);
        }

        liveRegion.textContent = `Showing ${loaded} of ${total} wrestlers`;
      }

      announceFilterResults(total) {
        let liveRegion = this.querySelector('.roster-grid__live-region');

        if (!liveRegion) {
          liveRegion = document.createElement('div');
          liveRegion.className = 'roster-grid__live-region visually-hidden';
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          this.appendChild(liveRegion);
        }

        if (total === 0) {
          liveRegion.textContent = 'No wrestlers found matching the selected filters';
        } else {
          liveRegion.textContent = `Filter applied. ${total} wrestler${total === 1 ? '' : 's'} found`;
        }
      }

      handleKeyDown(event) {
        // Only handle arrow keys within the grid
        if (!event.target.closest('.roster-card')) return;

        const cards = Array.from(this.gridContainer.querySelectorAll('.roster-card__link'));
        const currentIndex = cards.indexOf(event.target);

        if (currentIndex === -1) return;

        let nextIndex = -1;

        // Get computed grid columns
        const gridStyle = getComputedStyle(this.gridContainer);
        const columns = gridStyle.gridTemplateColumns.split(' ').length;

        switch (event.key) {
          case 'ArrowRight':
            nextIndex = Math.min(currentIndex + 1, cards.length - 1);
            break;
          case 'ArrowLeft':
            nextIndex = Math.max(currentIndex - 1, 0);
            break;
          case 'ArrowDown':
            nextIndex = Math.min(currentIndex + columns, cards.length - 1);
            break;
          case 'ArrowUp':
            nextIndex = Math.max(currentIndex - columns, 0);
            break;
          case 'Home':
            if (event.ctrlKey) {
              nextIndex = 0;
            }
            break;
          case 'End':
            if (event.ctrlKey) {
              nextIndex = cards.length - 1;
            }
            break;
          default:
            return;
        }

        if (nextIndex !== -1 && nextIndex !== currentIndex) {
          event.preventDefault();
          cards[nextIndex].focus();
        }
      }
 
      escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
    }
  );
}